/** @flow */
import React, { Component, Fragment, createRef } from "react";
import GridTable, { type ColConfig } from "./grid-table";
import classNames from "classnames";

type ScrollableGridTableProps<T> = {
  className?: string,
  selected?: Array<number>,
  data: Array<T>,
  config: {
    trStyle?: Object,
    cols: Array<ColConfig<T>>,
    leftCols?: Array<ColConfig<T>>,
    rightCols?: Array<ColConfig<T>>,
    idIdentifier?: string,
    onLineClick?: Function,
    lineClassName?: string,
    stickyHeaders?: boolean,
    clickToScrollInnerGrid?: boolean,
  },
  onHover?: (rowIndex: number) => void,
  updateOnHover?: boolean,
  alwaysShowScroll?: boolean, // Avoid using that prop
};

type State = {
  dataFit: boolean,
  activeRowIndex: number,
  scrollableTableWidth: ?number,
};

//TODO : Refactor this crazy component ---> use AgGrid instead
class ScrollableGridTable<T: Object> extends Component<ScrollableGridTableProps<T>, State> {
  state = {
    dataFit: false,
    activeRowIndex: -1,
    scrollableTableWidth: null,
  };

  containerRef = createRef();

  checkScroll = () => {
    const { current } = this.containerRef;
    if (!current) {
      return;
    }

    const containerWidth = current.clientWidth;
    let contentWidth = 0;
    const tables = current.querySelectorAll("table");
    const scrollablePart = current.querySelectorAll(".scrollable-wrapper");
    if (this.props.alwaysShowScroll && scrollablePart && scrollablePart.length) {
      const scrollablePartWidth = scrollablePart[0].clientWidth;

      this.setState(
        {
          scrollableTableWidth: scrollablePartWidth + scrollablePartWidth * 0.1,
        },
        () => {
          if (
            current.querySelectorAll(".scrollable-wrapper")[0].clientWidth >
            this.state.scrollableTableWidth
          ) {
            this.checkScroll();
          }
        },
      );
      return;
    }

    for (const table of tables) {
      contentWidth += table.clientWidth;
    }

    if (contentWidth <= containerWidth) {
      !this.state.dataFit &&
        this.setState({
          dataFit: true,
        });
    } else {
      this.state.dataFit &&
        this.setState({
          dataFit: false,
        });
    }
  };

  get scrollableTableStyle() {
    return this.state.scrollableTableWidth ? { width: `${this.state.scrollableTableWidth}px` } : {};
  }

  componentDidMount() {
    this.props.config.stickyHeaders && this.enableStickyHeaders();
    this.props.config.clickToScrollInnerGrid && this.clickToScrollInnerGrid();

    this.checkScroll();
    window.addEventListener("resize", this.checkScroll);
  }

  clickToScrollInnerGrid() {
    const { current } = this.containerRef;
    if (!current) {
      return;
    }

    let isDragging = false;
    let startCursorX;
    let startDraggableX;
    let startCursorY;
    let startDraggableY;

    const scrollableWrapper = current.querySelector(".scrollable-wrapper");
    const draggableTbody = scrollableWrapper.querySelector("tbody");

    if (!draggableTbody) return;

    draggableTbody.addEventListener("mousedown", e => {
      startCursorX = e.clientX;
      startCursorY = e.clientY;

      startDraggableX = scrollableWrapper.scrollLeft;
      startDraggableY = scrollableWrapper.scrollTop;

      draggableTbody.classList.add("dragging");
      isDragging = true;
    });

    draggableTbody.addEventListener("mouseup", e => {
      draggableTbody.classList.remove("dragging");
      isDragging = false;
    });
    draggableTbody.addEventListener("mouseleave", e => {
      draggableTbody.classList.remove("dragging");
      isDragging = false;
    });

    draggableTbody.addEventListener("mousemove", e => {
      if (!isDragging) return;

      e.preventDefault();

      const newX = startDraggableX + (startCursorX - e.clientX) * -1;
      const newY = startDraggableY + (startCursorY - e.clientY) * -1;

      scrollableWrapper.scroll(newX, newY);
    });
  }

  removeAllListeners() {
    window.removeEventListener("resize", this.checkScroll);

    if (this.props.config.stickyHeaders || this.props.config.clickToScrollInnerGrid) {
      const { current } = this.containerRef;
      if (!current) {
        return;
      }
      current.parentNode.replaceChild(current.cloneNode(true), current);
    }
  }

  enableStickyHeaders() {
    const { current } = this.containerRef;
    if (!current) {
      return;
    }
    const scrollableWrapper = current.querySelector(".scrollable-wrapper");

    if (scrollableWrapper) {
      current.addEventListener("scroll", () => {
        scrollableWrapper.scroll(scrollableWrapper.scrollLeft, current.scrollTop);
      });
      scrollableWrapper.addEventListener("scroll", () => {
        current.scroll(0, scrollableWrapper.scrollTop);
      });
    }
  }

  componentDidUpdate() {
    this.checkScroll();
  }

  componentWillUnmount() {
    this.removeAllListeners();
  }

  updateActiveRowIndex = (rowIndex: number) => {
    if (rowIndex === this.state.activeRowIndex) {
      return;
    }

    this.setState({
      activeRowIndex: rowIndex,
    });

    if (this.props.onHover) {
      this.props.onHover(rowIndex);
    }
  };

  render() {
    return (
      <div
        ref={this.containerRef}
        className={classNames(
          "d-flex scrollable ",
          this.props.className,
          this.props.config.stickyHeaders ? "sticky-headers" : "",
          {
            "sticky-actions": !this.state.dataFit,
          },
        )}
      >
        <Fragment>
          {this.props.config.leftCols && (
            <GridTable
              {...this.props}
              config={Object.assign({}, this.props.config, {
                cols: this.props.config.leftCols,
              })}
              updateOnHover={this.props.updateOnHover}
              onHover={this.updateActiveRowIndex}
              activeRowIndex={this.state.activeRowIndex}
              className="fixed-part left-cols"
            />
          )}
          <div className="scrollable-wrapper">
            <GridTable
              {...this.props}
              updateOnHover={this.props.updateOnHover}
              onHover={this.updateActiveRowIndex}
              activeRowIndex={this.state.activeRowIndex}
              className={classNames("scrollable-part w-100", {
                "scrollable-nowrap": this.props.alwaysShowScroll,
              })}
              style={this.scrollableTableStyle}
            />
          </div>
          {this.props.config.rightCols && (
            <GridTable
              {...this.props}
              config={Object.assign({}, this.props.config, {
                cols: this.props.config.rightCols,
              })}
              updateOnHover={this.props.updateOnHover}
              onHover={this.updateActiveRowIndex}
              activeRowIndex={this.state.activeRowIndex}
              className="fixed-part right-cols"
            />
          )}
        </Fragment>
      </div>
    );
  }
}

export default ScrollableGridTable;
